import { useMutation, useQuery } from "@apollo/client";
import {
	Checkbox,
	FormControlLabel,
	Grid,
	Typography,
	IconButton,
	Button,
	Fab,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	ImageList,
	ImageListItem,
	Box,
	CircularProgress,
	TextareaAutosize, // Import TextareaAutosize
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Machine } from "../graphql/querys/machine";
import { MuiFileInput } from "mui-file-input";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { CREATE_INTERVENTION } from "../graphql/mutation/createIntervention";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import { uploadInterventionPhotos } from "../services/intervention-photo-upload";

export default function ManutScreen() {
	const { t } = useTranslation();
	const { id } = useParams();
	const navigate = useNavigate();

	const { loading, error, data, refetch } = useQuery(Machine, {
		variables: { id },
	});
	const [createIntervention, { data: interventionData, loading: mutationLoading }] =
		useMutation(CREATE_INTERVENTION);

	useEffect(() => {
		if (interventionData) {
			navigate(`/machines-mobile/${id}`);
		}
	}, [interventionData, id, navigate]);

	// Image preview and file state
	const [previewImages, setPreviewImages] = useState([]);
	const [imgFiles, setImgFiles] = useState([]);
	const [isUploadingPhotos, setIsUploadingPhotos] = useState(false);
	const [uploadError, setUploadError] = useState("");

	const handleCreateIntervention = async () => {
		setUploadError("");
		setIsUploadingPhotos(true);

		const observations = [];

		for (const key in checkboxState) {
			if (checkboxState[key]) {
				if (key.startsWith("exchanged")) {
					const observation = key.replace("exchanged", "").trim() + " trocado";
					observations.push(observation);
				} else if (key.startsWith("rep")) {
					const observation = key.replace("rep", "").trim() + " reparado";
					observations.push(observation);
				}
			}
		}

		// Combine checkbox observations with user-entered observations
		const combinedObservations = observations.join(", ");
		const finalObservations = observationsField
			? `${combinedObservations}. ${observationsField}`
			: combinedObservations;

		try {
			const photoUrls = await uploadInterventionPhotos(imgFiles);
			await createIntervention({
				variables: {
					input: {
						type: "MANUTENTION",
						machineId: id,
						photosTaken: photoUrls,
						obs: finalObservations,
					},
				},
			});
		} catch (error) {
			setUploadError(error?.message || "Não foi possível guardar a manutenção.");
		} finally {
			setIsUploadingPhotos(false);
		}
	};

	const handleImageChange = (newFiles) => {
		const files = Array.isArray(newFiles)
			? newFiles
			: newFiles
				? [newFiles]
				: [];
		setImgFiles(files);
		setPreviewImages(files.map((file) => URL.createObjectURL(file)));
	};

	const handleRemoveImage = (indexToRemove) => {
		setPreviewImages(
			previewImages.filter((_, index) => index !== indexToRemove)
		);
		setImgFiles(imgFiles.filter((_, index) => index !== indexToRemove));
	};

	const CheckBoxs = [
		"Óleo do motor",
		"Filtro de óleo",
		"Filtro de Comb.1",
		"Filtro de Comb.2",
		"Filtro de ar",
		"Filtro de ar int",
	];

	const [checkboxState, setCheckboxState] = useState(
		CheckBoxs.reduce((init, value) => {
			return { ...init, [value]: false };
		}, {})
	);

	// New state for user-entered observations
	const [observationsField, setObservationsField] = useState("");

	if (loading || mutationLoading || isUploadingPhotos) {
		return (
			<Box
				sx={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					height: "100vh",
				}}>
				<CircularProgress />
			</Box>
		);
	}

	return (
		<Box p={4} bgcolor="#f4f4f4" minHeight="100vh">
			<Fab
				onClick={() => {
					navigate(`/machines-mobile/${id}`);
				}}
				sx={{
					margin: 0,
					top: 20,
					right: 20,
					position: "fixed",
				}}
				color="primary"
				aria-label={t("ACTIONS.BACK")}>
				<FirstPageIcon />
			</Fab>

			<Typography variant="h5" gutterBottom>
				{t("INTERVENTIONS.MAINTENANCE_TITLE")}
			</Typography>

			{/* Exchanged Section */}
			<Accordion defaultExpanded>
				<AccordionSummary expandIcon={<ExpandMoreIcon />}>
					<Typography>{t("INTERVENTIONS.EXCHANGED")}</Typography>
				</AccordionSummary>
				<AccordionDetails>
					{CheckBoxs.map((info) => (
						<FormControlLabel
							key={`exchanged${info}`}
							control={
								<Checkbox
									checked={checkboxState[`exchanged${info}`]}
									onChange={(event) => {
										setCheckboxState({
											...checkboxState,
											[`exchanged${info}`]: event.target.checked,
										});
									}}
								/>
							}
							label={info}
						/>
					))}
				</AccordionDetails>
			</Accordion>

			{/* Repaired Section */}
			<Accordion>
				<AccordionSummary expandIcon={<ExpandMoreIcon />}>
					<Typography>{t("INTERVENTIONS.REPAIRED")}</Typography>
				</AccordionSummary>
				<AccordionDetails>
					{CheckBoxs.map((info) => (
						<FormControlLabel
							key={`rep${info}`}
							control={
								<Checkbox
									checked={checkboxState[`rep${info}`]}
									onChange={(event) => {
										setCheckboxState({
											...checkboxState,
											[`rep${info}`]: event.target.checked,
										});
									}}
								/>
							}
							label={info}
						/>
					))}
				</AccordionDetails>
			</Accordion>

			{/* Observations Text Area */}
			<Box mt={3}>
				<Typography variant="subtitle2">
					{t("INTERVENTIONS.OBSERVATIONS")}
				</Typography>
				<TextareaAutosize
					minRows={5}
					value={observationsField}
					onChange={(e) => setObservationsField(e.target.value)}
					style={{
						width: "100%",
						border: "1px solid #ccc",
						borderRadius: "10px",
						padding: "10px",
						marginTop: "10px",
						fontSize: "16px",
						fontFamily: "Arial",
					}}
					placeholder={t("INTERVENTIONS.OBSERVATIONS_PLACEHOLDER")}
				/>
			</Box>

			{/* File Input Section */}
			<Box mt={5}>
				<MuiFileInput
					value={imgFiles}
					label={t("INTERVENTIONS.ADD_PHOTOS")}
					sx={{ width: "100%" }}
					multiple
					onChange={handleImageChange}
				/>
			</Box>

			{/* Image Previews */}
			{previewImages.length > 0 && (
				<ImageList cols={3} gap={8}>
					{previewImages.map((imageData, index) => (
						<ImageListItem key={index}>
							<img src={imageData} alt={`Preview ${index}`} />
							<IconButton
								aria-label={t("ACTIONS.DELETE")}
								onClick={() => handleRemoveImage(index)}
								style={{
									position: "absolute",
									top: 5,
									right: 5,
									color: "red",
								}}>
								<CloseIcon />
							</IconButton>
						</ImageListItem>
					))}
				</ImageList>
			)}
			{uploadError && (
				<Typography color="error" mt={2}>
					{uploadError}
				</Typography>
			)}

			{/* Save Button */}
			<Box mt={5} mb={5} textAlign="center">
				<Button
					variant="contained"
					onClick={handleCreateIntervention}
					disabled={mutationLoading}
					sx={{
						width: "50%",
						padding: "10px",
					}}>
					{t("ACTIONS.SAVE")}
				</Button>
			</Box>
		</Box>
	);
}
